import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CompanyInfoForm from './components/CompanyInfoForm';
import VerificationForm from './components/VerificationForm';
import ProfileCustomizationForm from './components/ProfileCustomizationForm';
import ContactSettingsForm from './components/ContactSettingsForm';

const EmployerRegistrationProfile = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending');

  const [employerData, setEmployerData] = useState({
    companyName: '',
    industry: '',
    companySize: '',
    headquarters: '',
    description: '',
    website: '',
    foundedYear: '',
    registrationNumber: '',
    taxId: '',
    documents: [],
    logo: null,
    brandColor: '#FF6B35',
    culturePhotos: [],
    videoUrl: '',
    offices: [{ location: '', address: '', phone: '' }],
    hrContacts: [{ name: '', email: '', phone: '', designation: '' }],
    socialMedia: { linkedin: '', twitter: '', facebook: '', instagram: '' },
    privacySettings: { profileVisibility: 'public', showContactInfo: true },
    communicationPreferences: { emailNotifications: true, smsNotifications: false }
  });

  const [errors, setErrors] = useState({});

  const steps = [
    { id: 1, label: 'Company Info', icon: 'Building2' },
    { id: 2, label: 'Verification', icon: 'Shield' },
    { id: 3, label: 'Customization', icon: 'Palette' },
    { id: 4, label: 'Contact & Settings', icon: 'Settings' }
  ];

  const handleDataChange = (field, value) => {
    setEmployerData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!employerData?.companyName) newErrors.companyName = 'Company name is required';
      if (!employerData?.industry) newErrors.industry = 'Industry is required';
      if (!employerData?.companySize) newErrors.companySize = 'Company size is required';
      if (!employerData?.headquarters) newErrors.headquarters = 'Headquarters location is required';
      if (!employerData?.description) newErrors.description = 'Company description is required';
    }
    
    if (step === 2) {
      if (!employerData?.registrationNumber) newErrors.registrationNumber = 'Registration number is required';
      if (!employerData?.taxId) newErrors.taxId = 'Tax ID is required';
      if (employerData?.documents?.length === 0) newErrors.documents = 'At least one verification document is required';
    }
    
    if (step === 4) {
      if (employerData?.offices?.length === 0 || !employerData?.offices?.[0]?.location) {
        newErrors.offices = 'At least one office location is required';
      }
      if (employerData?.hrContacts?.length === 0 || !employerData?.hrContacts?.[0]?.email) {
        newErrors.hrContacts = 'At least one HR contact is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      alert('Profile saved as draft successfully!');
    }, 1500);
  };

  const handleSubmitVerification = () => {
    if (validateStep(1) && validateStep(2)) {
      if (window.confirm('Submit your profile for verification? Our team will review within 2-3 business days.')) {
        setIsSaving(true);
        setTimeout(() => {
          setIsSaving(false);
          setVerificationStatus('under_review');
          alert('Profile submitted for verification! You will receive an email once approved.');
        }, 1500);
      }
    } else {
      alert('Please complete all required fields in Company Info and Verification sections.');
    }
  };

  const handlePublishProfile = () => {
    if (validateStep(1) && validateStep(2) && validateStep(4)) {
      if (verificationStatus !== 'approved') {
        alert('Your profile must be verified before publishing. Please submit for verification first.');
        return;
      }
      if (window.confirm('Publish your company profile? It will be visible to all candidates.')) {
        setIsSaving(true);
        setTimeout(() => {
          setIsSaving(false);
          alert('Company profile published successfully! Candidates can now discover your organization.');
        }, 1500);
      }
    } else {
      alert('Please complete all required sections before publishing.');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CompanyInfoForm
            formData={employerData}
            errors={errors}
            onChange={handleDataChange}
          />
        );
      case 2:
        return (
          <VerificationForm
            formData={employerData}
            errors={errors}
            onChange={handleDataChange}
            verificationStatus={verificationStatus}
          />
        );
      case 3:
        return (
          <ProfileCustomizationForm
            formData={employerData}
            errors={errors}
            onChange={handleDataChange}
          />
        );
      case 4:
        return (
          <ContactSettingsForm
            formData={employerData}
            errors={errors}
            onChange={handleDataChange}
          />
        );
      default:
        return null;
    }
  };

  const getVerificationBadge = () => {
    const badges = {
      pending: { color: 'bg-warning/10 text-warning border-warning/20', label: 'Pending Verification', icon: 'Clock' },
      under_review: { color: 'bg-accent/10 text-accent border-accent/20', label: 'Under Review', icon: 'Eye' },
      approved: { color: 'bg-success/10 text-success border-success/20', label: 'Verified', icon: 'CheckCircle' },
      rejected: { color: 'bg-error/10 text-error border-error/20', label: 'Verification Failed', icon: 'XCircle' }
    };
    return badges?.[verificationStatus] || badges?.pending;
  };

  const badge = getVerificationBadge();

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                Employer Registration & Profile
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Create your company profile and start hiring top talent
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full border flex items-center gap-2 ${badge?.color}`}>
              <Icon name={badge?.icon} size={16} />
              <span className="text-sm font-medium">{badge?.label}</span>
            </div>
          </div>
        </div>

        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => (
              <React.Fragment key={step?.id}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-smooth ${
                      currentStep >= step?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <Icon name={step?.icon} size={20} />
                  </div>
                  <span
                    className={`text-xs md:text-sm mt-2 font-medium ${
                      currentStep >= step?.id ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {step?.label}
                  </span>
                </div>
                {index < steps?.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mx-2 transition-smooth ${
                      currentStep > step?.id ? 'bg-primary' : 'bg-border'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-6 md:p-8 mb-6">
          {renderStepContent()}
        </div>

        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={isSaving}
            iconName="Save"
            iconPosition="left"
            className="flex-1 md:flex-initial"
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </Button>
          {currentStep < 4 ? (
            <Button
              onClick={handleNext}
              iconName="ChevronRight"
              iconPosition="right"
              className="flex-1"
            >
              Next Step
            </Button>
          ) : (
            <div className="flex flex-col md:flex-row gap-3 flex-1">
              <Button
                variant="secondary"
                onClick={handleSubmitVerification}
                disabled={isSaving || verificationStatus === 'approved'}
                iconName="Shield"
                iconPosition="left"
                className="flex-1"
              >
                Submit for Verification
              </Button>
              <Button
                onClick={handlePublishProfile}
                disabled={isSaving || verificationStatus !== 'approved'}
                iconName="Upload"
                iconPosition="left"
                className="flex-1"
              >
                Publish Profile
              </Button>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default EmployerRegistrationProfile;